/**
 * File operations — CRUD for db.files via the operations DI layer.
 *
 * All file storage goes through these operations so that both client
 * and server use the same getDb() dependency injection pattern.
 */

import { getDb } from "./runtime.js"
import type { OpResult } from "./op-types.js"
import { ok, fail } from "./op-types.js"
import type { DbFile } from "./db-types.js"
import { ObjectType } from "./db-types.js"

/**
 * Dexie Cloud's `@id` tables take a caller's own id only with the table's prefix (`fls` for files):
 * anything else is refused with "Primary '@' keys requires the key to be prefixed with \"fls\"".
 */
export const FILE_ID_PREFIX = "fls"

/** A file id: derived from `seed` (an idempotency key, a deterministic copy id) or random. */
export function newFileId(seed?: string): string {
  if (seed?.startsWith(FILE_ID_PREFIX)) return seed
  return FILE_ID_PREFIX + (seed ?? crypto.randomUUID()).replace(/-/g, "")
}

/** Metadata-only projection (no binary content/thumbnail). */
export type FileMeta = Omit<DbFile, "content" | "thumbnail">

/**
 * Run a read of db.files without fetching offloaded content. Dexie Cloud keeps large binaries in its
 * blob store and resolves a row's BlobRefs on every read (a download per file); a metadata read
 * drops the content anyway, so it must not pay for it.
 */
function withoutBlobResolve<T>(read: () => Promise<T>): Promise<T> {
  const db = getDb()
  return db.transaction("r", db.files, (tx) => {
    ;(tx as unknown as { idbtrans: { disableBlobResolve?: boolean } }).idbtrans.disableBlobResolve = true
    return read()
  })
}

function toMeta(file: DbFile): FileMeta {
  const { content: _c, thumbnail: _t, ...meta } = file
  // The addon's marker for unresolved BlobRefs is storage state, not metadata.
  delete (meta as { _hasBlobRefs?: unknown })._hasBlobRefs
  return meta
}

/**
 * Add a file record to db.files.
 *
 * Used by handleImageUpload (TipTap editor) and any other upload path.
 * Dexie Cloud automatically offloads binary content to blob storage.
 *
 * @returns The generated file ID (Dexie @id).
 */
export async function addFile(
  record: Omit<DbFile, "id"> & { id?: string },
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = await db.files.add(record as DbFile)
    return ok({ id: String(id) })
  } catch (e) {
    return fail(
      `addFile failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/**
 * Get a file by ID (full record including binary content).
 */
export async function getFile(
  id: string,
): Promise<OpResult<DbFile>> {
  const db = getDb()
  try {
    const file = await db.files.get(id)
    if (!file) return fail(`File not found: ${id}`)
    return ok(file)
  } catch (e) {
    return fail(
      `getFile failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/**
 * Get file metadata (without binary content/thumbnail).
 */
export async function getFileMeta(
  id: string,
): Promise<OpResult<FileMeta>> {
  const db = getDb()
  try {
    const file = await withoutBlobResolve(() => db.files.get(id))
    if (!file) return fail(`File not found: ${id}`)
    return ok(toMeta(file))
  } catch (e) {
    return fail(
      `getFileMeta failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/**
 * List files belonging to a parent object (task, project, note).
 * Returns metadata only (no binary content).
 */
export async function listFilesByParent(
  parentId: string,
): Promise<OpResult<FileMeta[]>> {
  const db = getDb()
  try {
    const files = await withoutBlobResolve(() => db.files.where("parentId").equals(parentId).toArray())
    return ok(files.map(toMeta))
  } catch (e) {
    return fail(
      `listFilesByParent failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

/**
 * Delete a file by ID.
 */
export async function deleteFile(
  id: string,
): Promise<OpResult<void>> {
  const db = getDb()
  try {
    await db.files.delete(id)
    return ok(undefined)
  } catch (e) {
    return fail(
      `deleteFile failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export type FileParentType = NonNullable<DbFile["parentType"]>

/** What a file may hang on, by the parent's object type. Areas, sections and the root may not. */
function fileParentTypeOf(type: ObjectType): FileParentType | undefined {
  switch (type) {
    case ObjectType.Task:
    case ObjectType.Subtask:
      return "task"
    case ObjectType.Project:
    case ObjectType.List:
      return "project"
    case ObjectType.Checklist:
      return "checklist"
    default:
      return undefined
  }
}

export interface AttachFileInput {
  parentId: string
  name: string
  mimeType: string
  content: Uint8Array
  thumbnail?: Uint8Array
  /** Seed of the file's id (an idempotent retry finds the file it created, see newFileId); default random. */
  id?: string
}

/**
 * Store a file on a task, subtask, project, collection or checklist item.
 *
 * The parent must exist and not be in the trash; the file takes the parent's realm, so a file on a
 * shared project is shared with it, and `parentType` comes from the parent, never from the caller.
 * `createdAt` is ISO like the app's own uploads. A missing parent fails with code `not_found`; a
 * storage failure's message starts with "addFile failed".
 */
export async function attachFile(
  input: AttachFileInput,
): Promise<OpResult<{ id: string; parentType: FileParentType }>> {
  const db = getDb()
  const parent = await db.totodo.get(input.parentId)
  if (!parent) return fail(`Parent not found: ${input.parentId}`, "not_found")
  if (parent.isDeleted === 1) return fail(`Parent is in the trash: ${input.parentId}`)
  const parentType = fileParentTypeOf(parent.type)
  if (!parentType) return fail("Files can be attached to tasks, subtasks, projects, collections and checklist items only")
  const userId = db.cloud.currentUserId
  const result = await addFile({
    id: newFileId(input.id),
    name: input.name,
    mimeType: input.mimeType,
    size: input.content.byteLength,
    content: input.content,
    ...(input.thumbnail ? { thumbnail: input.thumbnail } : {}),
    parentId: input.parentId,
    parentType,
    ...(parent.realmId ? { realmId: parent.realmId } : {}),
    createdAt: new Date().toISOString(),
    createdBy: userId ?? "",
  })
  if (!result.ok) return result
  return ok({ id: result.data.id, parentType })
}
